<script setup lang="ts">
import { computed, useTemplateRef, watch, type ComponentPublicInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { PAGE_SIZES } from '@/composables/useCourseList'
import type { CourseRow, CourseType, SortItem } from '@/types/courseList'

const props = defineProps<{
    rows: CourseRow[]
    itemsLength: number
    loading: boolean
    semester: string
    emptyMessage: string
    emptyHint?: string
    emptyAction?: string
}>()
const emit = defineEmits<{ 'click:empty-action': [] }>()
const page = defineModel<number>('page', { required: true })
const itemsPerPage = defineModel<number>('itemsPerPage', { required: true })
const sortBy = defineModel<SortItem[]>('sortBy', { required: true })

const TYPES: readonly CourseType[] = ['lec', 'tut', 'sem', 'lab']

/** Each type has its own container colour (Material 3 container roles plus two harmonised custom colours). */
const TYPE_COLORS: Record<CourseType, string> = {
    lec: 'primary-container',
    tut: 'tertiary-container',
    sem: 'green-container',
    lab: 'orange-container',
}

const { t, locale } = useI18n()

const headers = computed(() => [
    { title: t('courses.code'), key: 'code', sortable: true, width: 140 },
    { title: t('courses.name'), key: 'name', sortable: true },
    { title: t('courses.schedules'), key: 'types', sortable: false, width: 236 },
])

const hasHours = (row: CourseRow, type: CourseType) => row.range[type] > 0.01
const name = (row: CourseRow) => (locale.value === 'en' ? row.nameEn || row.nameCs : row.nameCs)
const otherName = (row: CourseRow) => {
    const other = locale.value === 'en' ? row.nameCs : row.nameEn
    return other && other !== name(row) ? other : ''
}
const schedule = (row: CourseRow, type: CourseType) => `/schedules/${props.semester}/${row.code}/${type}`
const rowProps = ({ item }: { item: CourseRow }) => ({ 'data-test': `course-row-${item.code}` })
/** On a wide screen the rows scroll inside the table (the page fills the window, page-fill.css); its header stays put. */
const table = useTemplateRef<ComponentPublicInstance>('table')
// A new page of rows (a search, a sort, another page or semester) starts at its top, as the page scroll used to.
watch(
    () => props.rows,
    () => {
        const scroller = (table.value?.$el as HTMLElement | undefined)?.querySelector('.v-table__wrapper')
        if (scroller) {
            scroller.scrollTop = 0
        }
    },
)
</script>

<template>
    <v-data-table-server
        ref="table"
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        v-model:sort-by="sortBy"
        mobile-breakpoint="sm"
        class="course-table"
        :headers="headers"
        :items="rows"
        :items-length="itemsLength"
        :items-per-page-options="[...PAGE_SIZES]"
        :loading="loading ? 'primary' : false"
        :row-props="rowProps"
        item-value="code"
        density="comfortable"
        fixed-header
        must-sort
        hover
    >
        <template v-if="rows.length === 0" #loading>
            <v-skeleton-loader type="table-row@5" class="bg-transparent" />
        </template>

        <template #[`item.code`]="{ item }">
            <span class="course-table__code" data-test="course-code">{{ item.code }}</span>
        </template>

        <template #[`item.name`]="{ item }">
            <div class="course-table__name" data-test="course-name">
                <span class="text-body-medium">{{ name(item) }}</span>
                <span v-if="otherName(item)" class="course-table__caption text-body-small text-medium-emphasis d-none d-sm-block">{{
                    otherName(item)
                }}</span>
            </div>
        </template>

        <template #[`item.types`]="{ item }">
            <div class="course-table__types">
                <v-chip
                    v-for="type in TYPES"
                    :key="type"
                    :to="schedule(item, type)"
                    :data-test="`type-${type}`"
                    :variant="hasHours(item, type) ? 'flat' : 'outlined'"
                    :color="hasHours(item, type) ? TYPE_COLORS[type] : undefined"
                    :class="['course-table__type', { 'course-table__type--empty': !hasHours(item, type) }]"
                    size="small"
                >
                    {{ t(`courses.typeShort.${type}`) }}
                    <v-tooltip activator="parent">
                        {{ t(`courses.types.${type}`) }}<template v-if="!hasHours(item, type)"> · {{ t('courses.noHours') }} </template>
                    </v-tooltip>
                </v-chip>
            </div>
        </template>

        <template #no-data>
            <v-empty-state
                data-test="empty"
                :icon="emptyAction ? 'mdi-text-search-variant' : 'mdi-book-open-blank-variant-outline'"
                :title="emptyMessage"
                :text="emptyHint"
                size="56"
                class="course-table__empty"
            >
                <template v-if="emptyAction" #actions>
                    <v-btn
                        data-test="clear-search"
                        variant="tonal"
                        color="primary"
                        prepend-icon="mdi-close"
                        @click="emit('click:empty-action')"
                    >
                        {{ emptyAction }}
                    </v-btn>
                </template>
            </v-empty-state>
        </template>
    </v-data-table-server>
</template>

<style scoped>
.course-table {
    background: transparent;
}

.course-table :deep(thead th) {
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    background: rgb(var(--v-theme-surface));
    white-space: nowrap;
}

/* The empty state sits in a table row; it is not a row to hover. */
.course-table :deep(.v-data-table-rows-no-data > td::after) {
    display: none;
}

.course-table :deep(.v-data-table-rows-no-data > td) {
    border-bottom: none !important;
}

.course-table__empty {
    padding-block: 48px;
}

.course-table__empty :deep(.v-empty-state__title) {
    font-size: 1.25rem;
    font-weight: 500;
}

.course-table__code {
    font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
    font-size: 0.875rem;
    font-weight: 500;
    letter-spacing: 0.02em;
}

.course-table__name {
    display: flex;
    flex-direction: column;
    padding-block: 10px;
    min-width: 220px;
}

.course-table__types {
    display: flex;
    gap: 6px;
    flex-wrap: nowrap;
}

.course-table__type {
    min-width: 48px;
    justify-content: center;
    font-weight: 500;
}

.course-table__type--empty {
    color: rgba(var(--v-theme-on-surface), 0.5);
    border-color: rgba(var(--v-theme-on-surface), 0.16);
    border-style: dashed;
}
</style>
